/**
 * Number formatting for engineering quantities.
 *
 * Tolerance analysis spans picofarads to megohms in the same project, so fixed
 * decimal places are useless. Engineering notation (exponents in multiples of
 * three, rendered as SI prefixes) is what the audience already reads.
 *
 * The unit is not a passive suffix: a value typed in `mV` already carries a
 * scale, and a prefix chosen without reading it produces `5.000 kmV`. So every
 * formatter here resolves the unit through `parseUnit` first and works in base
 * units, re-emitting exactly one prefix. Units that cannot take a prefix are
 * printed verbatim with no prefix at all.
 */

import { parseUnit, choosePrefix } from './units.ts';

/**
 * Fixed *significant* digits, keeping trailing zeros.
 *
 * Trailing zeros are meaningful here — `2.500 V` and `2.5 V` state different
 * things about how well the quantity is known, and a results table that drops
 * them reads as though precision varied row to row.
 */
function significant(value: number, digits: number): string {
  if (value === 0) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e6 || abs < 1e-4) return value.toExponential(digits - 1).replace('-', '−');
  const decimals = Math.min(20, Math.max(0, digits - (Math.floor(Math.log10(abs)) + 1)));
  return value.toFixed(decimals).replace('-', '−');
}

/**
 * Engineering notation, e.g. 4.700 kΩ, 12.50 µV.
 *
 * A prefixed unit is normalised: 5000 mV is 5.000 V, and 0.1 mm is 100.0 µm.
 * The displayed quantity is always equal to the value passed in — only its
 * spelling changes.
 */
export function eng(value: number, digits = 4, unit?: string): string {
  if (!Number.isFinite(value)) return value > 0 ? '∞' : value < 0 ? '−∞' : '—';

  const u = unit?.trim();
  const parsed = parseUnit(u);

  // No unit, or one that must not be prefixed (°C, %, psi, dB, counts).
  if (!parsed) {
    const text = significant(value, digits);
    return u ? `${text} ${u}` : text;
  }

  if (value === 0) return `0 ${parsed.base}`;

  const baseValue = value * parsed.scale;
  const { divisor, prefix } = choosePrefix(baseValue);
  const scaled = baseValue / divisor;
  const decimals = Math.min(
    20,
    Math.max(0, digits - (Math.floor(Math.log10(Math.abs(scaled))) + 1)),
  );

  return `${scaled.toFixed(decimals).replace('-', '−')} ${prefix}${parsed.base}`;
}

/**
 * A formatter locked to one prefix for a whole set of values.
 *
 * Axis ticks must not each choose their own prefix: a range of 0.5–1.5 V would
 * otherwise be labelled `500.0 mV, 1.000 V, 1.500 V`, which reads as three
 * different scales and makes the axis actively misleading. The prefix is
 * chosen once from the largest tick, and the decimal count with it, so the
 * labels line up.
 */
export function engAxis(values: number[], unit?: string, digits = 3): (value: number) => string {
  const u = unit?.trim();
  const parsed = parseUnit(u);
  const scale = parsed ? parsed.scale : 1;

  const magnitudes = values.map((v) => Math.abs(v) * scale).filter((v) => Number.isFinite(v));
  const peak = magnitudes.length > 0 ? Math.max(...magnitudes) : 0;

  const { divisor, prefix } = parsed ? choosePrefix(peak) : { divisor: 1, prefix: '' };
  const suffix = parsed ? `${prefix}${parsed.base}` : (u ?? '');

  const peakScaled = peak / divisor;
  const decimals =
    peakScaled > 0
      ? Math.min(20, Math.max(0, digits - (Math.floor(Math.log10(peakScaled)) + 1)))
      : 0;

  return (value: number) => {
    if (!Number.isFinite(value)) return '—';
    if (value === 0) return '0';
    const scaled = (value * scale) / divisor;
    const text = scaled.toFixed(decimals).replace('-', '−');
    return suffix ? `${text} ${suffix}` : text;
  };
}

/** Plain significant-figure formatting, for unitless ratios and coefficients. */
export function sig(value: number, digits = 4): string {
  if (!Number.isFinite(value)) return value > 0 ? '∞' : value < 0 ? '−∞' : '—';
  if (value === 0) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e6 || abs < 1e-4) return value.toExponential(digits - 1).replace('-', '−');
  return Number(value.toPrecision(digits)).toString().replace('-', '−');
}

export function percent(fraction: number, digits = 3): string {
  if (!Number.isFinite(fraction)) return '—';
  return `${(fraction * 100).toFixed(digits)}%`;
}

/**
 * Defects per million.
 *
 * Below one PPM the figure is reported with more precision, because that is the
 * range where the difference between 0.1 and 0.9 PPM is a real engineering
 * distinction rather than rounding.
 */
export function ppm(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  if (value < 1) return value.toPrecision(2);
  if (value < 1000) return value.toFixed(1);
  return Math.round(value).toLocaleString('en-US');
}

export function count(n: number): string {
  return n.toLocaleString('en-US');
}

export function duration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** Axis tick values at 1/2/5 x 10^n spacing covering [lo, hi]. */
export function niceTicks(lo: number, hi: number, target = 6): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / target;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / magnitude;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * magnitude;

  const ticks: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + step * 1e-9; t += step) {
    ticks.push(Math.abs(t) < step * 1e-9 ? 0 : t);
  }
  return ticks;
}
